import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import {
  createProfileRequestSchema,
  isReservedUserId,
  isValidUserIdFormat,
  type LoginResponse,
  randomTopicColor,
} from '@lifetimeline/shared';

import { Button } from '../components/Button.js';
import { Field } from '../components/Field.js';
import { Layout } from '../components/Layout.js';
import { Notice } from '../components/Notice.js';
import { useToast } from '../components/Toast.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { useI18n } from '../i18n/index.js';
import { PROFILE_URL_PREFIX } from '../lib/branding.js';
import { ApiError, api, errorMessageKey } from '../lib/apiClient.js';
import { clearLocalPicks, readLocalPicks } from '../lib/localPicks.js';
import { clearLocalImages } from '../lib/imageStore.js';
import { uploadLocalPicks } from '../features/merge/transferLocalPicks.js';
import styles from './pageStyles.module.css';

/**
 * 프로필 만들기 — `userId`가 여기서 확정된다.
 *
 * `userId`는 **바꿀 수 없다.** 공개 프로필 URL이자 DB 파티션 키라서다.
 * 그래서 입력 중에 규칙을 바로 알려주고, 무엇이 문제인지 구체적으로 말한다.
 */
/**
 * 가입하면 이 주제가 미리 생긴다. **하나뿐이다.**
 *
 * 여럿을 만들어 두면 안 쓸 것을 지우는 일부터 하게 된다. 하나면 "이렇게
 * 만드는 거구나"를 보여주면서도 치울 것이 없다.
 *
 * **여행인 이유**: 처음 들어온 사람이 당장 적을 수 있어야 한다. `일`·`일상`
 * 같은 말은 범위가 너무 넓어 무엇을 적을지가 안 떠오르는데, 여행은 다녀온
 * 해가 기억난다 — 주제를 만들기 전에 **기록부터** 적어 볼 수 있다.
 *
 * 이름은 **보는 사람의 언어로** 만들어 보낸다(`t()`). 서버는 그 사람이 어느
 * 언어로 가입했는지 모르고, 첫 주제는 화면 문구와 같은 말이어야 한다.
 */
const DEFAULT_TOPICS = ['timeline.seedTravel'] as const;

export function SignUpPage() {
  const { t } = useI18n();
  const { status, setProfile, acceptToken } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (status === 'anonymous') {
    return <Navigate to="/signin" replace />;
  }
  if (status === 'authenticated') {
    return <Navigate to="/me" replace />;
  }

  /**
   * 입력 중 검증.
   *
   * 서버도 같은 규칙으로 판정하지만(최종 책임은 서버), 제출해야만 알 수 있으면
   * 답답하다. 예약어와 형식은 여기서 바로 알려준다.
   */
  const userIdError = (() => {
    if (userId === '') {
      return null;
    }
    if (!isValidUserIdFormat(userId)) {
      return t('signUp.userIdInvalid');
    }
    if (isReservedUserId(userId)) {
      return t('signUp.userIdReserved');
    }
    return null;
  })();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const parsed = createProfileRequestSchema.safeParse({
      userId: userId.trim(),
      userName: userName.trim(),
    });
    if (!parsed.success) {
      setFormError(t('error.validation'));
      return;
    }

    setSubmitting(true);
    try {
      const created = await api.post<LoginResponse>('/api/me', parsed.data);

      // 가입하면 토큰이 새로 발급된다(pending 주체 → 진짜 userId).
      // 이걸 저장하지 않으면 이후 요청이 전부 401이 된다.
      acceptToken(created.token);
      if (created.user !== null) {
        setProfile(created.user);
      }

      /*
       * 주제 두 개를 **미리 만들어 둔다.**
       *
       * 주제가 없으면 기록을 걸 곳이 없어서, 가입하자마자 "주제를 먼저
       * 만드세요"부터 만난다 — 쓰기 시작하기 전에 설정부터 하라는 화면이다.
       * 두 개면 바로 적을 수 있고, 마음에 안 들면 이름만 고치면 된다.
       *
       * 이름을 **여기서** 만드는 이유: 서버는 보는 사람의 언어를 모른다.
       * 화면 문구와 같은 말로 시작해야 한다.
       *
       * 실패해도 가입을 막지 않는다 — 주제는 나중에 만들 수 있다.
       */
      for (const seed of DEFAULT_TOPICS) {
        try {
          await api.post('/api/me/timeline/topics', {
            label: t(seed),
            color: randomTopicColor(),
          });
        } catch {
          // 무시 — 주제 없이도 가입은 끝난다
        }
      }

      // 비로그인 때 고른 것들을 올린다. 새 계정이라 충돌이 있을 수 없다.
      // 사진이 브라우저 안의 Blob이라 먼저 올려서 주소를 받아야 한다.
      const localPicks = readLocalPicks();
      if (localPicks.length > 0) {
        const ready = await uploadLocalPicks(localPicks);
        await api.put('/api/me/picks', { picks: ready });

        /*
         * 순서도 옮긴다.
         *
         * 배치 저장은 자리를 정하지 않아, 이것만 하면 계정에서는 카테고리 id
         * 순으로 흩어진다 — 비로그인 때 공들여 맞춘 순서가 로그인하는 순간
         * 뒤섞인다. 새 계정이라 덮어쓸 기존 순서도 없다.
         *
         * 실패해도 가입을 막지 않는다. 목록은 이미 들어갔고 순서는 다시
         * 바꾸면 된다 — 순서 때문에 가입이 안 되는 편이 나쁘다.
         */
        try {
          await api.put('/api/me/picks/order', {
            categoryIds: ready.map((pick) => pick.categoryId),
          });
        } catch {
          // 무시 — 목록은 저장됐다
        }

        // 서버 저장이 끝난 뒤에만 비운다
        clearLocalPicks();
        void clearLocalImages();
      }

      toast.show(t('picks.saved'));
      navigate('/me', { replace: true });
    } catch (cause) {
      // 서버가 준 코드로 구체적인 안내를 고른다.
      // 409(중복)를 "입력이 잘못됐다"로 뭉뚱그리면 사용자는 형식을 고치려 든다.
      if (cause instanceof ApiError && cause.code === 2001) {
        setFormError(t('signUp.userIdTaken'));
      } else {
        setFormError(t(errorMessageKey(cause)));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = readLocalPicks().length;

  return (
    <Layout narrow>
      <div className={styles.head}>
        <h1 className={styles.title}>{t('signUp.title')}</h1>
        <p className={styles.subtitle}>{t('signUp.lead')}</p>
      </div>

      {pendingCount > 0 ? (
        <div className={styles.noticeSlot}>
          <Notice tone="info">{t('merge.autoAdded', { count: pendingCount })}</Notice>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} noValidate>
        <Field
          label={t('signUp.userIdLabel')}
          prefix={PROFILE_URL_PREFIX}
          value={userId}
          /*
           * 남의 이름을 넣어 두지 않는다 — 내 테스트 계정 이름이 그대로
           * 남아 있었다. 자리를 보여 주는 게 목적이니 **모양만** 보여준다:
           * 앞의 `life-timeline.com/@`와 이어 붙었을 때 주소처럼 읽히면 된다.
           */
          placeholder="yourname"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={20}
          autoFocus
          help={t('signUp.userIdHelp')}
          error={userIdError}
          // 대문자로 입력해도 소문자로 받는다 — 규칙을 알려주기 전에 맞춰준다
          onChange={(event) => setUserId(event.target.value.toLowerCase().trim())}
        />

        {/*
          이름 칸에는 **플레이스홀더를 두지 않는다.** 어떤 이름을 적어 두든
          그건 누군가의 이름이고, 라벨("표시할 이름")이 이미 할 말을 다 한다.
        */}
        <Field
          label={t('signUp.userNameLabel')}
          value={userName}
          maxLength={30}
          help={t('signUp.userNameHelp')}
          onChange={(event) => setUserName(event.target.value)}
        />

        {formError !== null ? <Notice tone="error">{formError}</Notice> : null}

        <div style={{ marginTop: 'var(--s5)' }}>
          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={submitting}
            disabled={userId === '' || userName.trim() === '' || userIdError !== null}
          >
            {t('signUp.submit')}
          </Button>
        </div>
      </form>
    </Layout>
  );
}
